import { createElement, Component } from "react"
import PropTypes from "prop-types"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import { get, omit, isObject, chain, pick, isFunction, some } from "lodash"
import Error from "components/error"
import Loading from "components/loading"
import { LOADING, ERROR, OK, UNKNOWN, combineStatuses } from "helpers/data-statuses"
import { log } from "helpers/logger"
import { Creators } from "model/data"
import { Creators as GraphqlCreators } from "model/graphql"
import { Creators as IntradayCreators } from "model/intraday"
import ScreenMiddleware from "hocs/screen"

function getGraphQLUrlQuery({ resource }, { query }) {
  if (window.location.hostname === "localhost" && resource) {
    return `# ${resource}\n${query}`
  }
  return query
}

function getQueryInfo(definition, props) {
  const url = definition.url(props)
  let channel
  if (isFunction(definition.intradayChannel)) {
    channel = definition.intradayChannel(props)
  }

  // Get request with url as ID
  if (!isObject(url)) {
    return { url, id: url, channel }
  }

  if (url.query) {
    // GraphQL query
    const { query, id, variables, options } = url
    return {
      isGraphQl: true,
      id: id || query,
      query: getGraphQLUrlQuery(definition, url),
      variables,
      account: props.account,
      options,
      channel,
    }
  }

  return {
    ...pick(url, ["url", "payload"]),
    id: url.id || url.url,
    channel,
  }
}

function loadData(newProps, definition, data) {
  const { globalResources, load, graphqlLoad, intradayQueries, registerQuery, unregisterQuery } = newProps
  const { view: { name: viewName } } = definition

  let missingFields = false

  if (definition.required) {
    missingFields = definition.required.some(field => newProps[field] === undefined || newProps[field] === null)
  }

  if (data && !missingFields) {
    chain(data)
      .filter(d => !d.intradayChannel)
      .forEach(definition => {
        const { resource, noToken, beta } = definition
        const status = get(globalResources, `${resource}.status`, UNKNOWN)
        const currentId = get(globalResources, `${resource}.id`, null)

        const { isGraphQl, id, url, payload, query, variables, account, options } = getQueryInfo(definition, newProps)

        if (status === UNKNOWN || currentId !== id) {
          if (isGraphQl) {
            graphqlLoad(resource, id, query, variables, account, options, beta)
          } else {
            load(resource, id, url, payload, noToken)
          }
        }
      })
      .value()

    chain(data)
      .filter(d => d.intradayChannel)
      .forEach(definition => {
        const { resource, keepDataHistory, beta } = definition

        const { channel, ...queryInfo } = getQueryInfo(definition, newProps)
        if (channel) {
          const currentId = get(intradayQueries, [channel, resource, "id"], null)

          if (currentId !== queryInfo.id) {
            if (queryInfo.id) {
              registerQuery(viewName, channel, resource, { ...queryInfo, beta, keepDataHistory })
            } else {
              unregisterQuery(viewName, channel, resource)
            }
          }
        } else {
          console.error(`No channel specified for query ${resource}`)
        }
      })
      .value()
  }
}

export default function createModule(definition) {
  const { data } = definition
  const ConnectedScreenMiddleware = connect(definition.mapStateToProps)(ScreenMiddleware)

  const ModuleWrapper = class extends Component {
    constructor(props, ...args) {
      super(props, ...args)
      this.state = { hasError: false }

      if (definition.view.getHeaderConfig) {
        const config = definition.view.getHeaderConfig(props)
        if (config.key !== props.appHeaderKey) {
          props.setHeader(config)
        }
      }
    }

    static propTypes = {
      load: PropTypes.func.isRequired,
      graphqlLoad: PropTypes.func.isRequired,
      setHeader: PropTypes.func,
      appHeaderKey: PropTypes.string,
      globalResources: PropTypes.object.isRequired,
      noSpinner: PropTypes.bool,
      unregisterModuleQueries: PropTypes.func.isRequired,
    }

    componentDidCatch(error) {
      this.setState({ hasError: true })
      log(error)
    }

    componentDidMount() {
      loadData(this.props, definition, data)
    }

    componentDidUpdate() {
      loadData(this.props, definition, data)
    }

    componentWillUnmount() {
      if (some(data, d => d.intradayChannel)) {
        const { unregisterModuleQueries } = this.props
        const { view: { name: viewName } } = definition

        unregisterModuleQueries(viewName)
      }
    }

    getStatus() {
      const { globalResources } = this.props

      if (data) {
        const statuses = chain(data)
          .filter(definition => {
            const { id } = getQueryInfo(definition, this.props)
            return !definition.intradayChannel || !!id
          })
          .map(definition => {
            if (definition.passthrough) {
              return OK
            }

            const { resource } = definition
            const currentId = get(globalResources, `${resource}.id`, null)

            const { id } = getQueryInfo(definition, this.props)

            if (currentId !== id) {
              return UNKNOWN
            } else {
              return get(globalResources, `${resource}.status`, UNKNOWN)
            }
          })
          .value()

        return combineStatuses(statuses)
      } else {
        return OK
      }
    }

    render() {
      const { props, state: { hasError } } = this
      const status = this.getStatus()

      if (status === LOADING && !props.noSpinner) {
        return <Loading />
      } else if (status === ERROR || hasError) {
        return definition.error ? createElement(definition.error) : <Error />
      } else {
        const { data } = definition
        const collections = {}

        if (data) {
          data.forEach(definition => {
            const { as, resource } = definition
            if (as !== null) {
              const currentId = get(props.globalResources, `${resource}.id`, null)
              const { id } = getQueryInfo(definition, props)

              if (id === currentId) {
                collections[as || resource] = get(props.globalResources, `${resource}.data`, null)
              }
            }
          })
        }

        return <ConnectedScreenMiddleware {...omit(props, ["globalResources", "load", "noSpinner"])} {...collections} status={status} View={definition.view} />
      }
    }
  }

  const mapStateToProps = (state, props) => {
    const result = definition.externalProps ? definition.externalProps(state, props) : {}
    result.globalResources = get(state, "global.data")
    result.intradayQueries = get(state, "global.intraday.queries", {})

    return result
  }

  const mapDispatchToProps = dispatch => bindActionCreators({
    load: Creators.load,
    graphqlLoad: GraphqlCreators.load,
    registerQuery: IntradayCreators.registerQuery,
    unregisterQuery: IntradayCreators.unregisterQuery,
    unregisterModuleQueries: IntradayCreators.unregisterModuleQueries,
    ...definition.actions,
  }, dispatch)

  ModuleWrapper.displayName = `ModuleWrapper(${definition.view.name || definition.name || "Module"})`
  return connect(mapStateToProps, mapDispatchToProps)(ModuleWrapper)
}
